import { ExpandLess, ExpandMore, SmartToy, Close } from '@mui/icons-material';
import { 
    Box, 
    Paper, 
    Typography, 
    IconButton, 
    Collapse, 
    Divider,
    useTheme
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ParsedAnalysis {
    analysisSummary: {
        transactionCount: number;
        timeframe: string;
        focusTransaction: string;
    };
    theIssue: {
        title: string;
        explanation: string;
        whatHappened: string[];
        beginnerNote?: string;
    };
    whatToLookFor: {
        items: Array<{
            title: string;
            description: string;
            checked?: boolean;
        }>;
    };
    nextSteps: {
        steps: Array<{
            title: string;
            description: string;
        }>;
        primaryAction?: string;
    };
    technicalDetails?: string;
}

interface AIResultsData {
    analysis: ParsedAnalysis;
    recommendations?: string[];
    issueType: string;
    assetSymbol?: string;
    transactionId?: string | number;
}

interface AIResultsPanelProps {
    isVisible: boolean;
    aiResults: AIResultsData | null;
    isMinimized: boolean;
    onToggle: () => void;
    onClose: () => void;
    inline?: boolean; // When true, removes bottom margin for horizontal layout
}

const AIResultsPanel: React.FC<AIResultsPanelProps> = ({
    isVisible,
    aiResults,
    isMinimized,
    onToggle,
    onClose,
    inline = false
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (!isVisible || !aiResults) {
        return null;
    }

    return (
        <Box sx={{ mb: inline ? 0 : 2, width: '100%' }}>
            <Paper
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    borderColor: theme.palette.mode === 'dark' 
                        ? 'rgba(144, 202, 249, 0.5)' 
                        : 'rgba(25, 118, 210, 0.3)',
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(144, 202, 249, 0.05)'
                        : 'rgba(25, 118, 210, 0.02)',
                }}
            >
                {/* Header - Always visible */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        pb: isMinimized ? 2 : 1,
                        cursor: 'pointer',
                    }}
                    onClick={onToggle}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToy 
                            sx={{ 
                                color: theme.palette.primary.main,
                                fontSize: '1.25rem'
                            }} 
                        />
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                fontWeight: 600,
                                color: theme.palette.text.primary
                            }}
                        >
                            {t('transactions.aiPanel.title', 'AI Analysis Results')}
                        </Typography>
                        {aiResults.assetSymbol && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.08)' 
                                        : 'rgba(0, 0, 0, 0.06)',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                }}
                            >
                                {aiResults.assetSymbol}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle();
                            }}
                            sx={{ mr: 1 }}
                        >
                            {isMinimized ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose();
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Content - Collapsible */}
                <Collapse in={!isMinimized}>
                    <Box>
                        <Divider />
                        <Box sx={{ p: 2, pt: 2 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    color: theme.palette.text.primary,
                                    '& strong': {
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                    },
                                    '& em': {
                                        fontStyle: 'italic',
                                        color: theme.palette.text.secondary,
                                    },
                                }}
                            >
                                {aiResults.analysis.theIssue.explanation}
                            </Typography>
                            
                            {aiResults.recommendations && aiResults.recommendations.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            mb: 1,
                                            color: theme.palette.text.primary
                                        }}
                                    >
                                        {t('transactions.aiPanel.recommendations', 'Recommendations:')}
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {aiResults.recommendations.map((rec, index) => (
                                            <Typography
                                                key={index}
                                                component="li"
                                                variant="body2"
                                                sx={{ 
                                                    mb: 0.5,
                                                    color: theme.palette.text.secondary
                                                }}
                                            >
                                                {rec}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Paper>
        </Box>
    );
};

export default AIResultsPanel;